import React from "react";

import { CLEANING_CHECKLIST_LANDING_PAGE } from "@config/links";

import { AVAILABLE_LANGS } from "@typings/globals";

import { BentoBox } from "@components/DataDisplay/BentoBox";

import { mediaQueriesV2 } from "@ui/themes/default";

import { useTranslate } from "@hooks/useTranslate";
import { useFeatureToggle } from "@hooks/useFeatureToggle";

import {
  desktopImage,
  largeDesktopImage,
  mobileImage,
  tabletImage,
} from "./CustomizeTaste.helpers";

const base64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAgCAYAAAAMq2gFAAAAAXNSR0IArs4c6QAABOBJREFUSEtllk9vHEUQxV919+yu+QaxvbYROSALoXAAgZSLFYREcjLiX5ASpAiSAxLiAFLEJ49nprvRe9U9M8RxWtVez9ZvXlV1VduLm+taSkGeJ8zjhGm8w/juHaa7O19TRZmBUqB/IQAxAmkXkfYH7PYnGE5OMBw+wHCgPUHa7xGHPcIwIMQIswB7+eSTWgWaMY+jgzrk7g7zNN8HJUMadnJIxzsBHEQ4V9ztENIG9OqbR7XUBpomzISMTc04Ik8T8pzBl3FFETGlBjpg2B8WJR0Ud/sFZCHCQoC9fvZFrVuQVI2YxzspzFSUM/iM8ScExDQ4aLcXKFGR7AFpRzUtbGmAgwz2x+3jWmtV6BS+aUQWaMQ8TSizg1ABM1cUuqLdXrAeLg8ZIbuWnyQQv2h//fREIDoTjKHqa55QqSYzbBVm5qGLCXEYBKFjWUIIIIi5SYOqZgH98+LpAiKMCliBRSGbBUKtMCoKDZSSwkfHykezVGILJMFYnhZc0dtXtxW1gCVOp8pHDxf3pcAIYtgsqFypiG9MVVIxDLDkVQbmJSY/A4TwPACwf3//ofKNGT6FidUlm3V4BGG13QO5qq6AFgIkVOYlBFTj4rcJevNzZaKpCqX6ydTeIQFc/F7QiiyG6AURYhKICuQ8RlkCSoPwNene3r7+pSrRlcatsZQFcSWRRWCEBERCtFoOgoepBjqPKGYo4B7ggZAGgv7+7XkVhL/w7Vuo4gZCkENWRTofzMNGQQYhhgxoKUAd9OevP1YCGEnlAlTgKmiTwhUWKzXttK95CO68GmbZipmp5rEhiBX75vmt5PQwhQ4wk/MU3DJktA5iyXpF8RWpJFdgJqBWTKUiF99TFQvNXn73rHY1roJhonPD0JSkSCDL2tWpd7Vk840dAgGmUjDT5iJFBPLHvv/2a1VgV0JQVzLEgCFEOMhVCcQvmAfcQ+Yq6JygbudMRb7s6c3jymbZq6uHihA6d1hAahCGrYNcjb91B4yEaWUpy2wEzP/NV58rdK7IQ9YB/wd52Pic2gTLuPbEd+cFY873QFL05WefriBCWuiGuAKpSOXNXsdpaX423ge5mrzA5sLW1kL36PrjDQhI1iutFYNC184Rm6NWB7XqUn5cyaKIRZHLmqPrhx+pGBg+VVurumETRg8n1TiEz7qiDUhF0MPGyvPFHKq8H15dNkXebgQSxPeCtM/499YjRWKadV601orrEH7OlxHow+O5H1iW+NIVGkQdYu0S/HsHqTU2kHeCdn6a7dUoEA/CxdnpcmBV4qjuvEObVRdvYeOLaay08qYzdoHeDVTWrSs4CLDj6YOlBamp0jFhfW+bUdF6orRIkedpOU8qdzZUgvxvS/c+P33QFLUBp/FQ1MUd1ju67zVSWqPsTgjqS4DN7wvoeHba5hGrqc0hwiphnCw+n3x8NFDLD4n8tF1d2iyC7Pq0P2PH8zONie3QI0DONWU3qw9Ivx2rzuWQjt9f/XM+xP8Xx/MNqDklQJDc7ApTcrbVp4MVUANhPj7WfZt0fOTy4rgZ5Q2gi0mHrDDeJZZp7HcOv+VQzXZGLXsfQFRrV5cE+eXENHuzlPAmZA2GjbJeDIssOqHjtvoNyAdjP+EEXV2oVv1ywtuPA1ZQv3b57cgV+a3V578Pwg7iHWLdu1o++B+hwqq905O4HgAAAABJRU5ErkJggg==";

export const CustomizeTaste: React.FC<
  Pick<React.ComponentPropsWithoutRef<"div">, "className"> & {
    language: AVAILABLE_LANGS;
  }
> = ({ className, language }) => {
  const translate = useTranslate("batmaid_pwa_static_homepage");
  const isCleaningChecklistAvailable = useFeatureToggle("cleaning_checklist");

  return (
    <BentoBox
      heading={translate("features_bento_customize_heading")}
      description={translate("features_bento_customize_description")}
      label={
        isCleaningChecklistAvailable
          ? undefined
          : translate("coming_soon_label")
      }
      cta={
        isCleaningChecklistAvailable
          ? translate("features_bento_customize_cta")
          : undefined
      }
      href={
        isCleaningChecklistAvailable
          ? `/${language}${CLEANING_CHECKLIST_LANDING_PAGE}`
          : undefined
      }
      variant="medium"
      className={className}
    >
      <BentoBox.Image
        width={1121}
        height={1084}
        src={mobileImage(language)}
        alt={translate("features_bento_customize_alt")}
        loading="lazy"
        base64={base64}
        sources={[
          {
            srcSet: mobileImage(language),
            media: mediaQueriesV2.largeMobile,
          },
          {
            srcSet: tabletImage(language),
            media: mediaQueriesV2.smallTablet,
          },
          {
            srcSet: tabletImage(language),
            media: mediaQueriesV2.tablet,
          },
          {
            srcSet: desktopImage(language),
            media: mediaQueriesV2.smallDesktop,
          },
          {
            srcSet: desktopImage(language),
            media: mediaQueriesV2.desktop,
          },
          {
            srcSet: largeDesktopImage(language),
            media: mediaQueriesV2.largeDesktop,
          },
        ]}
      />
    </BentoBox>
  );
};
